import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { Filter, Header } from "./component";
import { Tab, Nav } from "react-bootstrap";
import { QueueTab, TotalTabs } from "./component/ACDtabs";
import {
  getACDDailyTotalReport,
  getACDQueueReport,
  fetchConfigurations,
  exportDailyACDReport,
} from "actions";
import { isEmpty } from "lodash";

const DailyACD = () => {
  const [activeKey, setActiveKey] = useState("all");
  const [forceRefreshList, setForceRefreshList] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();

  const { direction } = useSelector((state) => state.configurationState.lists);
  const session = useSelector((s) => s.session);
  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  useEffect(() => {
    if (object_viewed_id) {
      dispatch(
        fetchConfigurations("direction, geography, business, language", {
          object_viewed_id,
        }),
      );
    }
  }, [dispatch, object_viewed_id]);

  const tabTitles = useMemo(() => {
    const titles = { all: "All", queue: "Queue" };
    if (direction) {
      direction.forEach((dir) => {
        titles[dir.id] = dir.value;
      });
    }
    return titles;
  }, [direction]);

  const currentTitle = tabTitles[activeKey] || "";

  const [extraFiltersState, setExtraFiltersState] = useState({
    values: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      program_id: [],
      selectedDate: new Date(), // Default to today
      formatDate: moment().format("YYYY-MM-DD"), // Default to today's date
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const programId = params.get("program_id");
    const month = params.get("month");
    const year = params.get("year");
    const directionId = params.get("direction_id");

    if (directionId) {
      setActiveKey(directionId);
    }

    if (programId && month && year) {
      const selectedDate = new Date(year, month - 1, 1);
      const formatDate = moment(selectedDate).format("YYYY-MM");
      setExtraFiltersState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          program_id: programId,
          month: month,
          year: year,
          selectedDate,
          formatDate,
        },
      }));
      setForceRefreshList(true);
    }
    // This hook should only run once on mount to check URL params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    if (activeKey === "queue") {
      handleQueueReportFilter();
    } else {
      handleDailyACDReportFilter();
    }
  };

  const handleDailyACDReportFilter = () => {
    // Guard: Do not dispatch if object_viewed_id is not ready
    if (!object_viewed_id) {
      return;
    }
    const direction_ids = activeKey === "all" ? ["all"] : [activeKey];
    dispatch(
      getACDDailyTotalReport({
        ...extraFiltersState.values,
        object_viewed_id,
        direction_ids,
      }),
    );
  };

  const handleQueueReportFilter = () => {
    // Guard: Do not dispatch if object_viewed_id is not ready
    if (!object_viewed_id) {
      return;
    }
    dispatch(
      getACDQueueReport({ ...extraFiltersState.values, object_viewed_id }),
    );
  };

  useEffect(() => {
    if (forceRefreshList) {
      handleFilter();
      setForceRefreshList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRefreshList]);

  useEffect(() => {
    // This effect now correctly waits for object_viewed_id
    handleFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, object_viewed_id]);

  const handleExport = () => {
    if (!object_viewed_id) {
      return;
    }
    dispatch(
      exportDailyACDReport({ ...extraFiltersState.values, object_viewed_id }),
    );
  };

  return (
    <div className="wrapper-div">
      <Header
        handleExport={handleExport}
        onFilter={handleFilter}
        extraFiltersState={extraFiltersState}
        setExtraFiltersState={setExtraFiltersState}
      />
      <Filter
        onFilter={handleFilter}
        extraFiltersState={extraFiltersState}
        setExtraFiltersState={setExtraFiltersState}
      />
      <div className="p-3">
        <div className="view-container">
          <Tab.Container
            activeKey={activeKey}
            onSelect={(k) => setActiveKey(k)}
          >
            {/* Top Nav Tabs */}
            <h4 className="font-16 font-weight-medium m-0 pt-3 px-3">
              {currentTitle}
            </h4>
            <div className="d-flex justify-content-between align-items-center p-3">
              <Nav
                variant="tabs"
                activeKey={activeKey}
                onSelect={(k) => setActiveKey(k)}
              >
                <Nav.Item>
                  <Nav.Link eventKey="all">All</Nav.Link>
                </Nav.Item>
                {!isEmpty(direction) &&
                  direction.map((dir) => (
                    <Nav.Item key={dir.id}>
                      <Nav.Link eventKey={dir.id}>{dir.value}</Nav.Link>
                    </Nav.Item>
                  ))}
                <Nav.Item>
                  <Nav.Link eventKey="queue">Queue</Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            {/* Tab Content */}
            <div>
              <Tab.Content>
                <Tab.Pane eventKey="all">
                  <TotalTabs
                    direction_id="all"
                    extraFiltersState={extraFiltersState}
                  />
                </Tab.Pane>
                {!isEmpty(direction) &&
                  direction.map((dir) => (
                    <Tab.Pane key={dir.id} eventKey={dir.id}>
                      <TotalTabs
                        direction_id={dir.id}
                        extraFiltersState={extraFiltersState}
                      />
                    </Tab.Pane>
                  ))}
                <Tab.Pane eventKey="queue">
                  <QueueTab extraFiltersState={extraFiltersState} />
                </Tab.Pane>
              </Tab.Content>
            </div>
          </Tab.Container>
        </div>
      </div>
    </div>
  );
};

export default DailyACD;
